import type { SessionStore } from '@/lib/auth/session-store.js';

import { ApiError, normalizeErrorBody } from './api-error.js';
import { API_PREFIX, DEFAULT_TIMEOUT_MS } from './base.js';
import type {
  HttpMethod,
  PathParamsOf,
  PathsWithMethod,
  QueryOf,
  RequestBodyOf,
  ResponseOf,
} from './http-types.js';
import type { paths } from './schema.js';

export interface AuthClientConfig {
  /** POST-путь обновления сессии. csrfGuard требует заголовок именно на нём. */
  refreshPath: PathsWithMethod<'post'>;
  /** Второй и последний путь, где обязателен X-CSRF-Token. */
  logoutPath: PathsWithMethod<'post'>;
  /**
   * Чтение CSRF-токена из cookie. Нужно на ХОЛОДНОЙ загрузке: в сторе токена ещё нет
   * (ответа для чтения не было), а cookie от прошлой сессии уже стоит, и без заголовка
   * csrfGuard ответит 403 — то есть сессию нельзя было бы восстановить после F5.
   */
  readCsrfCookie: () => string | null;
}

interface RawOptions {
  params?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
}

function buildPath(path: string, params?: Record<string, string>): string {
  if (params === undefined) return path;
  return path.replace(/\{([^}]+)\}/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined) throw new Error(`Missing path param "${key}" for ${path}`);
    return encodeURIComponent(value);
  });
}

function buildQueryString(query?: Record<string, string | number | boolean | undefined>): string {
  if (query === undefined) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/**
 * Один сетевой поход: без ретраев и без логики обновления токена.
 *
 * Таймаут сделан ручным AbortController, а НЕ через `AbortSignal.any([...])`: последний
 * не поддержан до iOS 17.4, а browserslist проекта держит пол `iOS >= 16.4` (package.json).
 * Внешний signal (отмена запроса из React Query при размонтировании) форвардится вручную.
 */
async function rawFetch(
  method: HttpMethod,
  path: string,
  init: { headers: Record<string, string>; body?: string; signal?: AbortSignal; timeoutMs: number },
): Promise<{ status: number; body: unknown }> {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, init.timeoutMs);
  const onExternalAbort = (): void => {
    controller.abort();
  };

  // Сигнал мог быть отменён ДО того, как мы повесили слушатель: тогда события уже не будет,
  // и без этой проверки запрос ушёл бы в сеть, хотя его отменили.
  if (init.signal?.aborted === true) controller.abort();
  init.signal?.addEventListener('abort', onExternalAbort);

  try {
    const res = await fetch(`${API_PREFIX}${path}`, {
      method: method.toUpperCase(),
      credentials: 'same-origin',
      headers: init.headers,
      body: init.body,
      signal: controller.signal,
    });

    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return { status: res.status, body: undefined };
    }

    const text = await res.text();
    if (text === '') return { status: res.status, body: undefined };
    try {
      return { status: res.status, body: JSON.parse(text) as unknown };
    } catch {
      /**
       * Не-JSON тело означает, что до API мы не дошли: прокси, балансировщик или WAF
       * вернул свою страницу с этим же статусом. Само приложение так ответить не может —
       * под `/api` стоит JSON-404 выше статики и SPA-fallback, а единственный выход из
       * обработчика ошибок это `res.json()`.
       *
       * ⚠️ Текст сообщения ТЕХНИЧЕСКИЙ и пользователю не показывается (см.
       * `TRANSPORT_ERROR_CODES` в api-error.ts). Он несёт статус и первую строку тела —
       * по ним видно, кто ответил вместо нас: страница ошибки nginx, челлендж Cloudflare
       * или что-то ещё. Раньше здесь была голая строка без единой подробности, и
       * разбираться приходилось форензикой по серверным логам.
       */
      throw new ApiError(
        res.status,
        'non_json_response',
        `Non-JSON response from ${path} (status ${res.status}, content-type ${
          res.headers.get('content-type') ?? 'none'
        })`,
        text.slice(0, 200),
        {},
      );
    }
  } catch (err: unknown) {
    if (err instanceof ApiError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Отмену снаружи (размонтирование компонента) нельзя выдавать за таймаут: React Query
      // ждёт именно AbortError, чтобы отличить отмену от ошибки и не показывать её пользователю.
      if (init.signal?.aborted === true) throw err;
      throw new ApiError(0, 'timeout', `Request to ${path} timed out`, undefined, {});
    }
    throw new ApiError(0, 'network_error', `Network request to ${path} failed`, err, {});
  } finally {
    clearTimeout(timer);
    init.signal?.removeEventListener('abort', onExternalAbort);
  }
}

export function createAuthClient<User>(store: SessionStore<User>, config: AuthClientConfig) {
  /**
   * Ровно два роута требуют X-CSRF-Token. Набор задан ТИПОМ `PathsWithMethod<'post'>`,
   * а не строками по коду: переименование пути в схеме без правки конфига не пройдёт
   * typecheck. Всё остальное аутентифицируется заголовком Authorization, который
   * кросс-сайтовая страница поставить не может, поэтому CSRF там не нужен.
   */
  const csrfRequired: ReadonlySet<string> = new Set<string>([config.refreshPath, config.logoutPath]);

  function currentCsrf(): string | null {
    return store.getCsrfToken() ?? config.readCsrfCookie();
  }

  let refreshPromise: Promise<boolean> | null = null;

  /**
   * Однополётное обновление сессии.
   *
   * Присваивание refreshPromise СИНХРОННО — оно происходит до первого await внутри
   * doRefresh(). JS однопоточен, поэтому между проверкой `if` и присваиванием ничего
   * вклиниться не может: пять параллельных 401 получают один и тот же промис, то есть
   * ровно один POST /auth/refresh уходит в сеть.
   */
  function refreshOnce(): Promise<boolean> {
    if (refreshPromise !== null) return refreshPromise;
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  }

  /**
   * ⚠️ Зовёт rawFetch НАПРЯМУЮ, минуя execute(). Это и есть защита от рекурсии: раз
   * обновление не проходит через оркестратор с правилом «на 401 обнови и повтори»,
   * оно физически не может запустить само себя.
   *
   * На провале стор чистится ОДИН раз здесь, а не в каждом ожидающем вызове: все они
   * получат один и тот же false от одного промиса и лишь бросят свою ошибку для своего
   * запроса.
   *
   * ⚠️ Редиректа здесь НЕТ намеренно. Императивный переход на /signin отсюда ломал бы
   * холодную загрузку: гость со старой CSRF-cookie от прошлой сессии был бы выброшен
   * с лендинга на экран входа без причины. Перенаправляет guard маршрута, увидев статус
   * `guest`, — он знает, защищённая это страница или публичная. Заодно это даёт ровно
   * один редирект при пяти параллельных отказах, без всякой синхронизации.
   */
  async function doRefresh(): Promise<boolean> {
    const csrf = currentCsrf();
    try {
      const { status, body } = await rawFetch('post', config.refreshPath, {
        headers: {
          'content-type': 'application/json',
          ...(csrf !== null ? { 'x-csrf-token': csrf } : {}),
        },
        // Пустой объект, а НЕ { refresh_token }: веб-режим носит refresh в cookie,
        // и одновременная передача cookie и тела даёт 400 ambiguous_credentials.
        body: '{}',
        timeoutMs: DEFAULT_TIMEOUT_MS,
      });
      if (status !== 200) {
        store.clear();
        return false;
      }
      const session = body as { user?: User; access_token: string; csrf_token?: string };

      /**
       * ⚠️ `user` в ответе refresh НЕОБЯЗАТЕЛЕН, и это не перестраховка: два пространства
       * токенов отвечают по-разному. `POST /auth/refresh` отдаёт профиль (он нужен
       * кабинету сразу), а `POST /admin/auth/refresh` — только `{access_token, expires_in}`.
       *
       * Без `?? previous` любое ФОНОВОЕ обновление токена посреди работы админки стирало бы
       * личность администратора: статус остался бы `authenticated`, а `user` стал бы
       * undefined — то есть имя в шапке пропадало бы само собой через десять минут, и
       * причину пришлось бы искать в клиенте API, а не в админке.
       */
      const previous = store.getSnapshot().user;
      const user = (session.user ?? previous) as User;

      store.setSession(user, session.access_token, session.csrf_token ?? null);
      return true;
    } catch {
      store.clear();
      return false;
    }
  }

  async function execute(
    method: HttpMethod,
    path: string,
    options: RawOptions,
    alreadyRetried: boolean,
  ): Promise<unknown> {
    const resolvedPath = buildPath(path, options.params) + buildQueryString(options.query);

    const headers: Record<string, string> = {};
    const accessToken = store.getAccessToken();
    if (accessToken !== null) headers.authorization = `Bearer ${accessToken}`;
    if (csrfRequired.has(path)) {
      const csrf = currentCsrf();
      if (csrf !== null) headers['x-csrf-token'] = csrf;
    }

    let body: string | undefined;
    if (options.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    const { status, body: responseBody } = await rawFetch(method, resolvedPath, {
      headers,
      body,
      signal: options.signal,
      timeoutMs: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });

    if (status >= 200 && status < 300) return responseBody;

    const apiError = normalizeErrorBody(status, responseBody);

    /**
     * Обновлением лечится ТОЛЬКО `token_expired`. Сервер отдаёт этот код исключительно
     * при ERR_JWT_EXPIRED (server/lib/auth/require-auth.ts); всё остальное — подделка,
     * чужое окружение, мусор — это `token_invalid`, и обновление его не чинит.
     * Автообновление на token_invalid замаскировало бы реальный баг вместо явного разлогина.
     *
     * Отсутствие бесконечного цикла гарантируют два условия: сам refresh идёт мимо этой
     * функции (см. doRefresh), а повторённый запрос помечен alreadyRetried и второй раз
     * обновляться не станет.
     */
    const canAutoRefresh =
      apiError.code === 'token_expired' && path !== config.refreshPath && !alreadyRetried;

    if (canAutoRefresh) {
      const refreshed = await refreshOnce();
      if (refreshed) return execute(method, path, options, true);
    }

    throw apiError;
  }

  async function apiFetch<Method extends HttpMethod, Path extends PathsWithMethod<Method>>(
    method: Method,
    path: Path,
    options: {
      params?: PathParamsOf<paths[Path][Method]>;
      query?: QueryOf<paths[Path][Method]>;
      body?: RequestBodyOf<paths[Path][Method]>;
      signal?: AbortSignal;
      timeoutMs?: number;
    } = {},
  ): Promise<ResponseOf<paths[Path][Method]>> {
    return execute(method, path, options as RawOptions, false) as Promise<
      ResponseOf<paths[Path][Method]>
    >;
  }

  return { apiFetch, refreshOnce, store };
}
